<script setup>
//#region imports
import { nextTick, ref } from 'vue';
import { cloneDeep, isEmpty, merge } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useTablePaginator, useTableSort } from '@/composables/useTableLS';
import { usePermissions } from '@/composables/usePermissions';
import updateUrl from '@/service/updateUrl';
import { useDetectPermission } from '@/composables/useDetectPermission';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};
const LS_PATH = 'AffiliatesNotifications';
const fields = [
  {
    name: 'created_at',
    width: '140',
    sortable: 'custom',
    align: 'left',
    format: 'date-time',
  },
  {
    name: 'title',
    minWidth: '320',
    width: '320',
    class: 't:flex t:space-x-4 t:items-center',
    computedValueHtml(row) {
      const content = `<div>${row.title}</div>`;
      const warning = '<i class="fas fa-exclamation-triangle" style="color: var(--text-negative)" />';

      return row.is_required_to_read ? content + warning : content;
    },
  },
  {
    name: 'message',
    class: 'text',
    minWidth: '425',
  },
];
//#endregion

//#region composables
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { hasPermission } = usePermissions();
const { checkRequest, checkRoute } = useDetectPermission();
const route = useRoute();
const api = useApi();
//#endregion

//#region state
const tableData = ref([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const errors = ref({});
//#endregion

//#region methods
function pageChange(pageVal, size) {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
}
function handleSort({ prop, order }) {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  };
}

function _completedFilterUrl() {
  const _dataFilters = _query();
  _dataFilters.page = page.value;

  delete _dataFilters.offset;

  updateUrl.updateGetParams(_dataFilters);
}

function fetchData() {
  if (!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  errors.value = {};

  _completedFilterUrl();
  const options = _query();

  api.getAffiliatesNotifications(options).then((response) => {
    count.value = response.data.misc.count;
    tableData.value = cloneDeep(response.data.payload);
  }).catch((e) => {
    errors.value = e.data.errors;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
}

function initData() {
  if (checkRequest('affiliates_notifications_view')) {
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!isEmpty(_urlData)) {
      setPage(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      merge(optionsSort.value, _urlData);
    } else {
      setSort(_urlData);
    }
    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('affiliates_notifications_view');
}

function handleOpenSendNotificationPopup() {
  const popupManager = usePopupManager();

  popupManager.sendNotification.open({
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
}

function handlePreviewNotification(data) {
  const popupManager = usePopupManager();
  popupManager.viewNotification.open({
    data: {
      popupData: data,
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="affiliates-notifications">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('affiliates.notifications.title') }}
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="hasPermission('affiliates_notifications_edit')"
          filled
          icon="bell"
          @click="handleOpenSendNotificationPopup"
        >
          {{ $t('affiliates.notifications.popup.sendNotification') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      class="affiliates-notifications__table"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="affiliates.notifications"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="80"
        >
          <template v-slot:header>
            <i class="fas fa-check" />
          </template>
          <template v-slot="scope">
            <div
              v-if="scope.row.status === 'pending'"
              style="word-break: normal"
            >
              {{ $t('affiliates.notifications.sending') }}
            </div>
            <div
              v-else
              class="affiliates-notifications__table-read-count"
              :class="{
                'affiliates-notifications__table-read-count_success': scope.row.read_count === scope.row.sent_count,
              }"
            >
              {{ scope.row.read_count }}/{{ scope.row.sent_count }}
            </div>
          </template>
        </ElTableColumn>
        <ElTableColumn
          align="center"
          width="74"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              @click="handlePreviewNotification(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.affiliates-notifications {
  &__table-read-count{
    color: var(--text-warning);
    font-size: 12px;

    &_success {
      color: var(--text-positive);
    }
  }
}
</style>
